import React from "react";
import { PageHeader, SectionCard, EmptyState } from "../../components/Ui";
import { ArrowUpRight } from "lucide-react";

/**
 * Generic "Coming soon" tab for the Accounts workspace.
 * Used for Journal Entries / Payments / Receipts / Reports until real flows ship.
 */
export default function AccountsTabStub({
  eyebrow = "Accounts module",
  title,
  description,
  icon: Icon,
  features = [],
  testId,
}) {
  return (
    <div className="space-y-5 sm:space-y-6 min-w-0" data-testid={testId}>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,320px)] gap-4 lg:gap-6 items-start">
        <SectionCard title="Workspace preview" testId={`${testId}-preview`}>
          <EmptyState
            icon={Icon}
            title={`${title} is coming soon`}
            description="The workspace and data model are reserved. Ledger entries, attachments, and audit trail will plug into this tab without any layout shifts."
          />
          {features.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.28em] text-primary font-medium mb-3">
                What will live here
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {features.map((f) => (
                  <li
                    key={f.title}
                    className="rounded-lg border border-border/70 bg-muted/30 px-3.5 py-2.5"
                  >
                    <p className="text-[13px] font-medium text-foreground leading-tight">{f.title}</p>
                    {f.hint && <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{f.hint}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Why this exists" testId={`${testId}-context`}>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The Accounts workspace is being expanded into a full accounting suite.
            Each sub-module shares the same head tabs so adding a new flow does not disturb
            existing screens or muscle-memory.
          </p>
          <div className="mt-4 rounded-lg border border-bansal-gold/30 bg-bansal-gold/10 px-3.5 py-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-bansal-goldDark font-medium">
              <ArrowUpRight className="h-3.5 w-3.5" /> Next up
            </div>
            <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed">
              Journal entries, payment vouchers, party receipts, and a unified
              report center — all reachable from the Accounts head tabs.
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
